import React, { useMemo } from "react";
import { Tag, Typography } from "antd";

import { ago } from "@components/Date";
import { type PersonalAccessToken } from "@api/types";

import getStatusTag from "./getStatusTag";
import TableRowActions from "../TableRowActions";

const LABEL_NAME = "Name";
const LABEL_STATUS = "Status";
const LABEL_EXPIRES_AT = "Expires";
const LABEL_LAST_USED_AT = "Last Used";
const LABEL_EMPTY = "-";

type Props = {
  onSelected: (selectedToken: PersonalAccessToken) => void;
  onRevoked: (revokedToken: PersonalAccessToken) => void;
};

// NOTE: the config keeps its identity while the handlers keep theirs. The
//       table sorts and indexes the items again for each new config, therefore
//       an unstable config makes the table load the items on each render.
const useColumnsConfig = ({ onSelected, onRevoked }: Props) => {
  const columnsConfig = useMemo(() => {
    const nameColumnProps = {
      key: "name",
      title: LABEL_NAME,
      render: (name: string, personalAccessToken: PersonalAccessToken) => (
        <Typography.Link
          strong
          onClick={() => onSelected(personalAccessToken)}
        >
          {name}
        </Typography.Link>
      ),
      isSearchable: true,
    };

    const statusColumnProps = {
      key: "status",
      title: LABEL_STATUS,
      render: (status: string, { expiresAt }: PersonalAccessToken) => {
        const { color, label } = getStatusTag({ status, expiresAt });

        return <Tag color={color}>{label}</Tag>;
      },
    };

    const expiresAtColumnProps = {
      key: "expiresAt",
      title: LABEL_EXPIRES_AT,
      render: (value: string) => ago(value, true),
    };

    const lastUsedAtColumnProps = {
      key: "lastUsedAt",
      title: LABEL_LAST_USED_AT,
      render: (value?: string) => {
        const hasValue = !!value;

        if (!hasValue) {
          return LABEL_EMPTY;
        }

        return ago(value, true);
      },
    };

    const actionsColumnProps = {
      key: "actions",
      title: false,
      width: 48,
      render: (_: unknown, personalAccessToken: PersonalAccessToken) => (
        <TableRowActions
          personalAccessToken={personalAccessToken}
          onRevoked={onRevoked}
        />
      ),
    };

    return [
      { ...nameColumnProps },
      { ...lastUsedAtColumnProps },
      { ...expiresAtColumnProps },
      { ...statusColumnProps },
      { ...actionsColumnProps },
    ];
  }, [
    onRevoked,
    onSelected,
  ]);

  return columnsConfig;
};

export default useColumnsConfig;
